import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaEye, FaEyeSlash, FaTrash } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { readAvatarImage } from '../../utils/imageUpload';
import { updateUserPassword, updateUserProfile } from '../../services/userProfile';
import { setCurrentActorName } from '../../services/activityLog';
import ProfileAvatar from './ProfileAvatar';
import './profileSettingsModal.css';

export default function ProfileSettingsModal() {
  const {
    showProfileSettings,
    closeProfileSettings,
    userEmail,
    userProfile,
    refreshUserProfile,
  } = useAuth();

  const fileInputRef = useRef(null);
  const [displayName, setDisplayName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [passwordCurrent, setPasswordCurrent] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordCurrent, setShowPasswordCurrent] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [photoSelected, setPhotoSelected] = useState(false);
  const wasOpenRef = useRef(false);

  const hasProfileChanges =
    photoSelected ||
    displayName !== (userProfile.displayName || '') ||
    avatarUrl !== (userProfile.avatarUrl || '');

  useEffect(() => {
    if (!showProfileSettings) {
      wasOpenRef.current = false;
      return;
    }

    if (!wasOpenRef.current) {
      wasOpenRef.current = true;
      setDisplayName(userProfile.displayName || '');
      setAvatarUrl(userProfile.avatarUrl || '');
      setPasswordCurrent('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      setSuccess('');
      setPhotoSelected(false);
      setShowPasswordCurrent(false);
      setShowNewPassword(false);
    }
  }, [showProfileSettings, userProfile.displayName, userProfile.avatarUrl]);

  if (!showProfileSettings) return null;

  const handleAvatarPick = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) return;

    try {
      const dataUrl = await readAvatarImage(file);
      setAvatarUrl(dataUrl);
      setPhotoSelected(true);
      setSuccess('');
      setError('');
    } catch (pickError) {
      setError(pickError.message || 'Could not load that image.');
    }
  };

  const handleSaveProfile = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!hasProfileChanges) return;

    setSubmitting(true);

    const result = await updateUserProfile(userEmail, {
      displayName,
      avatarUrl,
    });

    setSubmitting(false);

    if (!result.ok) {
      setError(result.error);
      return;
    }

    const profile = await refreshUserProfile();
    setAvatarUrl(profile.avatarUrl || '');
    setDisplayName(profile.displayName || '');
    setCurrentActorName(profile.displayName || '');
    setPhotoSelected(false);
    setSuccess('Profile saved.');
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!passwordCurrent) {
      setError('Enter your current password to change it.');
      return;
    }

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }

    setSubmitting(true);

    const result = await updateUserPassword(userEmail, passwordCurrent, newPassword);

    setSubmitting(false);

    if (!result.ok) {
      setError(result.error);
      return;
    }

    setPasswordCurrent('');
    setNewPassword('');
    setConfirmPassword('');
    setSuccess('Password updated.');
  };

  return createPortal(
    <div className="profile-settings-overlay" onClick={closeProfileSettings} role="presentation">
      <div
        className="profile-settings-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="profile-settings-title"
        aria-modal="true"
      >
        <button
          type="button"
          className="profile-settings-close"
          onClick={closeProfileSettings}
          aria-label="Close"
        >
          &times;
        </button>

        <h2 id="profile-settings-title" className="profile-settings-title">Account settings</h2>
        <p className="profile-settings-subtitle">{userEmail}</p>

        <form className="profile-settings-form" onSubmit={handleSaveProfile}>
          <section className="profile-settings-section">
            <h3 className="profile-settings-section-title">Profile photo</h3>
            <div className="profile-settings-avatar-row">
              <div className="profile-settings-avatar-preview">
                <ProfileAvatar
                  email={userEmail}
                  displayName={displayName}
                  avatarUrl={avatarUrl}
                  className="profile-settings-avatar-fallback"
                  imageClassName="profile-settings-avatar-fallback"
                />
              </div>
              <div className="profile-settings-avatar-actions">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="profile-settings-file-input"
                  onChange={handleAvatarPick}
                />
                <button
                  type="button"
                  className="profile-settings-btn profile-settings-btn--secondary"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Upload photo
                </button>
                {avatarUrl && (
                  <button
                    type="button"
                    className="profile-settings-btn profile-settings-btn--ghost"
                    onClick={() => {
                      setAvatarUrl('');
                      setPhotoSelected(true);
                    }}
                  >
                    <FaTrash aria-hidden="true" />
                    Remove photo
                  </button>
                )}
              </div>
            </div>
            {photoSelected && (
              <p className="profile-settings-hint profile-settings-hint--photo">
                Photo ready — click Save changes below.
              </p>
            )}
          </section>

          <section className="profile-settings-section">
            <h3 className="profile-settings-section-title">Display name</h3>
            <label className="profile-settings-label">
              Name
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="profile-settings-input"
                placeholder="Your name"
                autoComplete="name"
              />
            </label>
          </section>

          <button
            type="submit"
            className="profile-settings-btn profile-settings-btn--primary profile-settings-btn--full"
            disabled={submitting || !hasProfileChanges}
          >
            {submitting ? 'Saving…' : 'Save changes'}
          </button>
        </form>

        <form className="profile-settings-form" onSubmit={handleChangePassword}>
          <section className="profile-settings-section">
            <h3 className="profile-settings-section-title">Change password</h3>
            <label className="profile-settings-label">
              Current password
              <span className="profile-settings-password-wrap">
                <input
                  type={showPasswordCurrent ? 'text' : 'password'}
                  value={passwordCurrent}
                  onChange={(e) => setPasswordCurrent(e.target.value)}
                  className="profile-settings-input profile-settings-input--password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="profile-settings-password-toggle"
                  onClick={() => setShowPasswordCurrent((visible) => !visible)}
                  aria-label={showPasswordCurrent ? 'Hide password' : 'Show password'}
                >
                  {showPasswordCurrent ? <FaEyeSlash /> : <FaEye />}
                </button>
              </span>
            </label>
            <label className="profile-settings-label">
              New password
              <span className="profile-settings-password-wrap">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="profile-settings-input profile-settings-input--password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="profile-settings-password-toggle"
                  onClick={() => setShowNewPassword((visible) => !visible)}
                  aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                >
                  {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </span>
            </label>
            <label className="profile-settings-label">
              Confirm new password
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="profile-settings-input"
                autoComplete="new-password"
              />
            </label>
            <button
              type="submit"
              className="profile-settings-btn profile-settings-btn--secondary profile-settings-btn--full"
              disabled={submitting}
            >
              {submitting ? 'Updating…' : 'Update password'}
            </button>
          </section>
        </form>

        {error && <p className="profile-settings-error">{error}</p>}
        {success && <p className="profile-settings-success">{success}</p>}
      </div>
    </div>,
    document.body
  );
}
